import { useState } from "react";
import { motion } from "framer-motion";
import { Gem, ShoppingBag, Egg } from "lucide-react";
import { PackageOrderModal } from "./order/PackageOrderModal";

const packages = [
  {
    id: "duchess",
    name: "The Duchess",
    price: 225,
    eggs: 1,
    scoops: 1,
    purses: "3 Mystical Diamond Capsules",
    description: "1 Golden Egg (1 Purse Set), 1 scoop of magical pearls, and 3 mystical diamond capsules",
    featured: false,
  },
  {
    id: "countess",
    name: "The Countess",
    price: 325,
    eggs: 1,
    scoops: 2,
    purses: "3 Mystical Diamond Capsules",
    description: "1 Golden Egg (1 Purse Set), 2 scoops of magical pearls, and 3 mystical diamond capsules",
    featured: false,
  },
  {
    id: "princess",
    name: "The Princess",
    price: 425,
    eggs: 2,
    scoops: 1,
    purses: "3 Mystical Diamond Capsules",
    description: "2 Golden Eggs (2 Purse Sets), 1 scoop of magical pearls, and 3 mystical diamond capsules",
    featured: true,
  },
  {
    id: "marquess",
    name: "The Marquess",
    price: 525,
    eggs: 2,
    scoops: 2,
    purses: "3 Mystical Diamond Capsules",
    description: "2 Golden Eggs (2 Purse Sets), 2 scoops of magical pearls, and 3 mystical diamond capsules",
    featured: false,
  },
  {
    id: "empress",
    name: "The Empress",
    price: 625,
    eggs: 3,
    scoops: 1,
    purses: "3 Mystical Diamond Capsules",
    description: "3 Golden Eggs (3 Purse Sets), 1 scoop of magical pearls, and 3 mystical diamond capsules",
    featured: false,
  },
  {
    id: "queen",
    name: "The Queen",
    price: 725,
    eggs: 3,
    scoops: 2,
    purses: "3 Mystical Diamond Capsules",
    description: "3 Golden Eggs (3 Purse Sets), 2 scoops of magical pearls, and 3 mystical diamond capsules",
    featured: true,
  },
];

const MENU_IMAGE = "/menu.png";

export default function MenuSection() {
  const [activePkg, setActivePkg] = useState(null);
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-24 md:py-32 relative"
    >
      <div className="absolute inset-0 section-glow-gold" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">
            Luxury Awaits
          </p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">
            Mystery Scoop Packages
          </h2>
          <div className="gold-line w-24 mx-auto mt-6" />
          <p className="font-montserrat text-white/40 text-sm mt-6 max-w-xl mx-auto">
            Each golden egg is hand selected and may reveal designer-inspired
            treasures, beautiful accessories, and surprise gifts!
          </p>
        </motion.div>

        {/* Menu Image — placed right after the description */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex justify-center mb-16"
        >
          <img
            src={MENU_IMAGE}
            alt="Luxury Mystery Scoop Offers"
            className="max-w-md w-full rounded-xl border border-[#D4AF37]/30 shadow-2xl"
            data-testid="menu-image"
          />
        </motion.div>

        {/* Package Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`package-card-${pkg.id}`}
              className={`relative group rounded-xl border overflow-hidden transition-colors duration-500 ${
                pkg.featured
                  ? "border-[#D4AF37]/60 bg-[#121212]"
                  : "border-[#D4AF37]/20 bg-[#0f0f0f]"
              } hover:border-[#D4AF37]`}
            >
              {pkg.featured && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F7EF8A] to-[#D4AF37]" />
              )}

              <div className="p-8">
                {/* Package name */}
                <p className="font-great-vibes text-[#FF007F]/80 text-lg mb-1">
                  {pkg.name}
                </p>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-playfair text-[#D4AF37] text-4xl font-bold">
                    ${pkg.price}
                  </span>
                </div>

                {/* Divider */}
                <div className="gold-line w-full mb-6" />

                {/* Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Egg
                      className="w-4 h-4 text-[#D4AF37]/60 shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="font-montserrat text-white/60 text-sm">
                      {pkg.eggs} Golden Egg{pkg.eggs > 1 ? "s" : ""} ({pkg.eggs} Purse Set{pkg.eggs > 1 ? "s" : ""})
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Gem
                      className="w-4 h-4 text-[#FF007F]/60 shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="font-montserrat text-white/60 text-sm">
                      {pkg.scoops} Scoop{pkg.scoops > 1 ? "s" : ""} of Magical
                      Pearls
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ShoppingBag
                      className="w-4 h-4 text-[#D4AF37]/60 shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="font-montserrat text-white/60 text-sm">
                      {pkg.purses}
                    </span>
                  </div>
                </div>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => setActivePkg(pkg.id)}
                  data-testid={`package-order-btn-${pkg.id}`}
                  className={`mt-8 block w-full text-center py-3 rounded-full text-sm font-montserrat tracking-widest uppercase transition-all duration-300 ${
                    pkg.featured
                      ? "btn-gold"
                      : "border-2 border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                >
                  Order Now
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {activePkg && <PackageOrderModal packageId={activePkg} onClose={() => setActivePkg(null)} />}
    </section>
  );
}
